import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import logo from './logo.png';
import MenuUser from './MenuUser';
import { useAuth0 } from '@auth0/auth0-react';
import Menu from '@mui/material/Menu';
import { useState } from 'react';
import { MenuItemType, Props } from './types';
import {
  ExploresContainer,
  LoginButton,
  responsiveAppBarStyles,
  SmallScreenBoxExplores,
} from './styles';
import { LoggedInAppBar } from './LoggedInAppBar';
import { ENTERPRISE_IMAGE_PATH, NAVBAR_ACTIVE_TEXT_COLOR } from '../../theme';
import { isEnterprise, SVG_ICON_COLOR } from '../../theme';
import { ExploreSvg, HubSvg } from '../SVGIcons';
import { IconButton, MenuItem, Typography } from '@mui/material';
import Mixpanel from '../../utils/mixpanel';
import { useHideOnScroll } from '../../hooks/useHideOnScroll';
import { useIsMobile } from '../../hooks/useIsMobile';

const userMenu: MenuItemType[] = [
  {
    value: <MenuUser user={''} imageUrl={''} subscriptionTier={0} />,
    id: 1,
    route: '',
  },
  { value: 'My NFTs', id: 5, route: 'my-nfts' },
  { value: 'Manage Shares', id: 2, route: 'share-management' },
  { value: 'Billing', id: 6, route: 'billing' },
  { value: 'Settings', id: 4, route: 'profile' },
  { value: 'Log Out', id: 7, route: '' },
];

export const userPages = [
  {
    name: 'Tracks',
    route: '/my-tracks',
    component: <HubSvg color={SVG_ICON_COLOR} />,
  },
  {
    name: 'Explore',
    route: '/explore',
    component: <ExploreSvg color={SVG_ICON_COLOR} />,
  },
];

const ResponsiveNavbar: React.FC<Props> = ({ currentUser }) => {
  const [selectedPublicPage, setSelectedPublicPage] = useState<string>('');
  const [anchorElNav, setAnchorElNav] = useState<(EventTarget & HTMLButtonElement) | null>(null);
  const [anchorElUser, setAnchorElUser] = useState<(EventTarget & HTMLButtonElement) | null>(null);

  const { loginWithRedirect, isAuthenticated, logout, user } = useAuth0();
  const navigate = useNavigate();
  // Hide the fixed bar on scroll-down (phones only) to reclaim space.
  const isMobile = useIsMobile();
  const barHidden = useHideOnScroll();

  // WelcomePage IS the sign-up/log-in choice -- App.tsx renders it for all
  // three of these paths. Repeating the pair up here gave that screen two
  // "Sign up"s and two "Log in"s, and the navbar's Sign up is `contained`, so
  // it competed with the page's own primary CTA for the same click. The page
  // wins; the bar keeps only the logo and Explore.
  const { pathname } = useLocation();
  const isWelcomeRoute = ['/welcome', '/signup', '/logout'].includes(pathname);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorElUser) {
      setAnchorElUser(null);
    }
    if (anchorElNav) {
      setAnchorElNav(null);
    } else {
      setAnchorElNav(event.currentTarget);
    }
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorElNav) {
      setAnchorElNav(null);
    }
    if (anchorElUser) {
      setAnchorElUser(null);
    } else {
      setAnchorElUser(event.currentTarget);
    }
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = (item: MenuItemType) => {
    if (item.value === 'Log Out') {
      logout({
        logoutParams: { returnTo: window.location.origin + '/logout' },
      });
      sessionStorage.clear();
      Mixpanel.reset();
    }
    setAnchorElUser(null);
  };

  //when no user is logged in
  const notLoggedIn = () => {
    return (
      <AppBar
        sx={{
          ...responsiveAppBarStyles.appBar,
          transform: isMobile && barHidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.25s ease',
          willChange: 'transform',
        }}
        position="static"
      >
        <Container maxWidth="xl">
          <Toolbar>
            <SmallScreenBoxExplores>
              <IconButton
                size="large"
                onClick={handleOpenNavMenu}
                color="inherit"
                disableFocusRipple
                disableRipple
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={responsiveAppBarStyles.menu}
                MenuListProps={{
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  },
                }}
              >
                <Button
                  size="small"
                  sx={responsiveAppBarStyles.loginButton}
                  variant="outlined"
                  onClick={() =>
                    loginWithRedirect({
                      appState: { returnTo: '/my-tracks' },
                    })
                  }
                >
                  Log in
                </Button>
                {userPages
                  .filter((page) => page.name === 'Explore' || page.name === 'Artists')
                  .map((userPage) => (
                    <MenuItem
                      key={userPage.name}
                      sx={responsiveAppBarStyles.mediumScreenMenuIcon}
                      onClick={() => {
                        setSelectedPublicPage(userPage.name);
                        navigate(userPage.route);
                        handleCloseNavMenu();
                      }}
                    >
                      {userPage.component}
                      &nbsp;&nbsp;
                      {userPage.name}
                    </MenuItem>
                  ))}
              </Menu>
            </SmallScreenBoxExplores>
            <Box sx={responsiveAppBarStyles.toolbarImageContainer}>
              <Link to="/my-tracks">
                <img
                  style={{ width: '150px' }}
                  className="logo-image"
                  src={isEnterprise ? ENTERPRISE_IMAGE_PATH : logo}
                  alt="logo"
                />
              </Link>
              <ExploresContainer>
                <Typography
                  onClick={() => {
                    setSelectedPublicPage('Explore');
                    navigate('/explore');
                  }}
                  style={
                    selectedPublicPage === 'Explore' ? { color: NAVBAR_ACTIVE_TEXT_COLOR } : {}
                  }
                  sx={responsiveAppBarStyles.publicPage}
                >
                  Explore
                </Typography>
              </ExploresContainer>
            </Box>
            {!isWelcomeRoute && (
              <>
                <LoginButton
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    loginWithRedirect({
                      appState: { returnTo: '/my-tracks' },
                    })
                  }
                >
                  Log in
                </LoginButton>
                <Button
                  sx={responsiveAppBarStyles.signupButton}
                  size="small"
                  variant="contained"
                  onClick={() =>
                    loginWithRedirect({
                      appState: { returnTo: '/initial-setting' },
                      authorizationParams: { screen_hint: 'signup' },
                    })
                  }
                >
                  Sign up
                </Button>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    );
  };

  //when user is logged in
  const LoggedIn = () => {
    return (
      <>
        <LoggedInAppBar
          currentUser={currentUser}
          userPages={userPages}
          handleOpenNavMenu={handleOpenNavMenu}
          anchorElNav={anchorElNav}
          handleCloseNavMenu={handleCloseNavMenu}
          handleOpenUserMenu={handleOpenUserMenu}
          anchorElUser={anchorElUser}
          user={user}
          userMenu={userMenu}
          handleCloseUserMenu={handleCloseUserMenu}
          setAnchorElUser={setAnchorElUser}
        />
      </>
    );
  };
  if (isAuthenticated) return LoggedIn();
  return notLoggedIn();
};
export default ResponsiveNavbar;
